import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Avatar, Box, Breadcrumbs, Button, Link, Menu, MenuItem, Toolbar, Typography } from '@mui/material';
import AppBar from '@mui/material/AppBar';
import { alpha } from '@mui/material/styles';
import { useAtomValue } from 'jotai';
import type { ReactNode } from 'react';
import { useState } from 'react';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import { sidebarCollapsedAtom } from '@consentropy/shared';
import { ThemeToggle } from '@consentropy/shared';
import { SIDEBAR_COLLAPSED_WIDTH, SIDEBAR_WIDTH } from './Sidebar';
import { useAuth } from '@consentropy/shared';
import { ROUTE_PATHS, conversationDetailPath } from '../../routes/routePaths';
import { useProject } from '../../hooks/useProjects';
import { useConversation } from '../../hooks/useConversations';

const SEGMENT_LABELS: Record<string, string> = {
  dashboard: 'Dashboard',
  projects: 'Projects',
  admin: 'Admin',
  clients: 'Client Management',
  users: 'User Management',
  profile: 'Profile',
  settings: 'Settings',
  conversations: 'Conversations',
  'ai-chat': 'AI Assistant',
  knowledge: 'Knowledge Query',
  registers: 'Registers',
};

/** Renders the label for a project breadcrumb, loading the project name when available */
function ProjectBreadcrumbLabel({ projectId }: { projectId: string }) {
  const { data: project } = useProject(projectId);
  return <>{project?.name ?? 'Project'}</>;
}

/** Renders the label for a conversation breadcrumb, loading the conversation title when available */
function ConversationBreadcrumbLabel({ conversationId }: { conversationId: string }) {
  const { data: conversation } = useConversation(conversationId);
  return <>{conversation?.title ?? 'Conversation'}</>;
}

/** Renders the right-side breadcrumbs based on the current path */
function DynamicBreadcrumbs() {
  const location = useLocation();

  const segments = location.pathname.split('/').filter(Boolean);

  const projectsIndex = segments.indexOf('projects');
  const projectIdFromUrl = projectsIndex !== -1 ? segments[projectsIndex + 1] : undefined;
  const conversationsIndex = segments.indexOf('conversations');
  const conversationIdFromUrl = conversationsIndex !== -1 ? segments[conversationsIndex + 1] : undefined;

  // A conversation can be nested under a parent (and that parent under its own parent), but the
  // URL only ever encodes the leaf conversation id, so the ancestor chain has to be resolved
  // separately and spliced into the breadcrumb trail.
  const { data: conversation } = useConversation(conversationIdFromUrl ?? '');
  const { data: parentConversation } = useConversation(conversation?.childOfConversationId ?? '');
  const { data: grandparentConversation } = useConversation(parentConversation?.childOfConversationId ?? '');

  const ancestorConversations = [grandparentConversation, parentConversation].filter(
    (item): item is NonNullable<typeof item> => Boolean(item),
  );

  const crumbs: { label: ReactNode; to?: string }[] = [
    { label: 'Dashboard', to: ROUTE_PATHS.DASHBOARD },
  ];

  segments.forEach((seg, i) => {
    const path = '/' + segments.slice(0, i + 1).join('/');

    if (seg === 'dashboard') return; // already added as root

    // Project id segment — use the project name when available
    if (i > 0 && segments[i - 1] === 'projects') {
      crumbs.push({ label: <ProjectBreadcrumbLabel projectId={seg} />, to: path });
      return;
    }

    // Conversation id segment — insert any ancestor conversations first, then the conversation itself
    if (i > 0 && segments[i - 1] === 'conversations') {
      if (projectIdFromUrl) {
        ancestorConversations.forEach((ancestor) => {
          crumbs.push({
            label: ancestor.title,
            to: conversationDetailPath(projectIdFromUrl, ancestor.id),
          });
        });
      }
      crumbs.push({ label: <ConversationBreadcrumbLabel conversationId={seg} />, to: path });
      return;
    }

    const label = SEGMENT_LABELS[seg];
    if (label) {
      crumbs.push({ label, to: path });
    }
  });

  if (crumbs.length <= 1) return null;

  return (
    <Breadcrumbs separator="/" aria-label="breadcrumb" sx={{ fontSize: '0.75rem' }}>
      {crumbs.map((crumb, i) => {
        const isLast = i === crumbs.length - 1;
        if (isLast || !crumb.to) {
          return (
            <Typography
              key={crumb.to ?? String(i)}
              sx={{
                color: "text.primary",
                fontSize: "inherit"
              }}>
              {crumb.label}
            </Typography>
          );
        }
        return (
          <Link
            key={crumb.to}
            component={RouterLink}
            underline="hover"
            color="inherit"
            to={crumb.to}
            sx={{
              fontSize: "inherit"
            }}
          >
            {crumb.label}
          </Link>
        );
      })}
    </Breadcrumbs>
  );
}

export function TopBar({ companyName }: { companyName?: string }) {
  const collapsed = useAtomValue(sidebarCollapsedAtom);
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const sidebarWidth = collapsed ? SIDEBAR_COLLAPSED_WIDTH : SIDEBAR_WIDTH;

  const onLogout = async () => {
    await logout();
    navigate(ROUTE_PATHS.LOGIN, { replace: true });
  };

  return (
    <AppBar
      position="fixed"
      elevation={0}
      sx={(theme) => ({
        width: `calc(100% - ${sidebarWidth}px)`,
        ml: `${sidebarWidth}px`,
        transition: 'width 0.2s ease, margin-left 0.2s ease',
        backgroundColor: theme.palette.background.default,
        borderBottom: `1px solid ${theme.palette.divider}`,
        color: theme.palette.text.primary,
        zIndex: theme.zIndex.drawer + 1,
      })}
    >
      <Toolbar sx={{ gap: 2 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
          <Typography
            variant="h6"
            sx={(t) => ({
              fontWeight: 800,
              color: t.palette.primary.main,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              ...(t.palette.mode === 'dark' && {
                textShadow: `0 0 16px ${alpha(t.palette.primary.main, 0.35)}`,
              }),
            })}
          >
            {companyName ?? 'CONSENTROPY'}
          </Typography>
          <DynamicBreadcrumbs />
        </Box>

        <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 1 }}>
          <ThemeToggle />
          <Button
            onClick={(event) => setAnchorEl(event.currentTarget)}
            endIcon={<ExpandMoreIcon />}
            sx={(t) => ({ color: t.palette.text.primary, fontWeight: 700 })}
          >
            <Avatar sx={(t) => ({
              width: 28,
              height: 28,
              bgcolor: t.palette.primary.main,
              color: t.palette.getContrastText(t.palette.primary.main),
              mr: 1,
              ...(t.palette.mode === 'dark' && {
                boxShadow: `0 0 8px ${alpha(t.palette.primary.main, 0.4)}`,
              }),
            })}>
              {user?.name?.charAt(0).toUpperCase() ?? 'U'}
            </Avatar>
            {user?.name ?? 'User'}
          </Button>
          <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
            <MenuItem component={RouterLink} to={ROUTE_PATHS.PROFILE} onClick={() => setAnchorEl(null)}>
              Profile
            </MenuItem>
            <MenuItem onClick={onLogout}>Logout</MenuItem>
          </Menu>
        </Box>
      </Toolbar>
    </AppBar>
  );
}
